import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  setDoc,
  doc, 
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { SeasonSnapshot, Points, Achievement, UserBadge, PersonalRecord, TeamMember } from '../types';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';
import { pointsService } from './pointsService';
import { badgeService } from './badgeService';
import { avatarService } from './avatarService';
import { activityService } from './activityService';
import { teamService } from './teamService';

const DEBUG_SEASON_SERVICE = false;
const debugLog = (...args: unknown[]) => {
  if (DEBUG_SEASON_SERVICE) {
    console.log(...args);
  }
};

export const seasonService = {
  // Get the last season snapshot to determine date range for current season
  async getLastSeasonSnapshot(userId: string, teamId: string): Promise<SeasonSnapshot | null> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'seasonSnapshots',
          [
            { field: 'userId', op: '==', value: userId },
            { field: 'teamId', op: '==', value: teamId }
          ],
          1 // Only need the most recent one
        );
        
        if (documents.length === 0) {
          return null;
        }
        
        // Sort by resetDate descending and get first
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().resetDate?.seconds || 0;
          const bTime = b.data().resetDate?.seconds || 0;
          return bTime - aTime;
        });
        
        const docData = sorted[0].data();
        return {
          id: sorted[0].id,
          ...docData,
          resetDate: docData.resetDate ? Timestamp.fromDate(new Date(docData.resetDate.seconds * 1000)) : Timestamp.now(),
          createdAt: docData.createdAt ? Timestamp.fromDate(new Date(docData.createdAt.seconds * 1000)) : Timestamp.now(),
        } as SeasonSnapshot;
      }
      
      // On web/Android, try SDK first, fallback to REST API on permission errors
      try {
        const q = query(
          collection(db, 'seasonSnapshots'),
          where('userId', '==', userId),
          where('teamId', '==', teamId),
          orderBy('resetDate', 'desc'),
          limit(1)
        );
        
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
          return null;
        }
        
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          resetDate: doc.data().resetDate,
          createdAt: doc.data().createdAt,
        } as SeasonSnapshot;
      } catch (sdkError: any) {
        // If SDK fails with permission error, try REST API as fallback
        if (sdkError.code === 'permission-denied' || sdkError.message?.includes('permission')) {
          console.log('[SeasonService] SDK permission error, falling back to REST API');
          try {
            const documents = await firestoreRestApi.queryDocuments(
              'seasonSnapshots',
              [
                { field: 'userId', op: '==', value: userId },
                { field: 'teamId', op: '==', value: teamId }
              ],
              1
            );
            
            if (documents.length === 0) {
              return null;
            }
            
            // Sort by resetDate descending and get first
            const sorted = documents.sort((a, b) => {
              const aTime = a.data().resetDate?.seconds || 0;
              const bTime = b.data().resetDate?.seconds || 0;
              return bTime - aTime;
            });
            
            const docData = sorted[0].data();
            return {
              id: sorted[0].id,
              ...docData,
              resetDate: docData.resetDate ? Timestamp.fromDate(new Date(docData.resetDate.seconds * 1000)) : Timestamp.now(),
              createdAt: docData.createdAt ? Timestamp.fromDate(new Date(docData.createdAt.seconds * 1000)) : Timestamp.now(),
            } as SeasonSnapshot;
          } catch (restError: any) {
            // If REST API also fails with permission error, it's likely the first reset (no snapshots exist)
            // Return null to allow the reset to proceed using team member join date or default date range
            if (restError.message?.includes('403') || restError.message?.includes('permission')) {
              console.log('[SeasonService] REST API also failed with permission error, assuming no snapshots exist yet');
              return null;
            }
            throw restError;
          }
        }
        throw sdkError; // Re-throw if it's not a permission error
      }
    } catch (error) {
      console.error('Error getting last season snapshot:', error);
      return null;
    }
  },

  // Get team member join date to determine season start if no previous snapshot
  async getTeamMemberJoinDate(userId: string, teamId: string): Promise<Date | null> {
    try {
      const members = await teamService.getTeamMembers(teamId);
      const member = members.find(m => m.athleteId === userId);
      if (member && member.joinedAt) {
        return member.joinedAt.toDate();
      }
      return null;
    } catch (error) {
      console.error('Error getting team member join date:', error);
      return null;
    }
  },

  // Filter achievements by date range
  filterAchievementsByDate(achievements: Achievement[], startDate: Date, endDate: Date): Achievement[] {
    return achievements.filter(achievement => {
      if (!achievement.unlockedAt) return false;
      const unlockedDate = achievement.unlockedAt.toDate();
      return unlockedDate >= startDate && unlockedDate <= endDate;
    });
  },

  // Filter badges by date range
  filterBadgesByDate(badges: UserBadge[], startDate: Date, endDate: Date): UserBadge[] {
    return badges.filter(badge => {
      if (!badge.awardedAt) return false;
      const awardedDate = badge.awardedAt.toDate();
      return awardedDate >= startDate && awardedDate <= endDate;
    });
  },

  // Filter PRs by date range
  filterPRsByDate(prs: PersonalRecord[], startDate: Date, endDate: Date): PersonalRecord[] {
    return prs.filter(pr => {
      if (!pr.date) return false;
      const prDate = pr.date.toDate();
      return prDate >= startDate && prDate <= endDate;
    });
  },

  // Count activities in date range
  async countActivitiesInRange(userId: string, teamId: string, startDate: Date, endDate: Date): Promise<number> {
    try {
      // Get all activities for user (with high limit)
      const activities = await activityService.getUserActivities(userId, 1000, teamId);
      
      // Filter by date range
      const filtered = activities.filter(activity => {
        const activityDate = new Date(activity.date + 'T12:00:00');
        return activityDate >= startDate && activityDate <= endDate;
      });
      
      return filtered.length;
    } catch (error) {
      console.error('Error counting activities:', error);
      return 0;
    }
  },

  // Reset a single user's season
  async resetUserSeason(
    userId: string, 
    teamId: string, 
    seasonName: string, 
    resetBy: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      debugLog('Starting season reset for user:', userId, 'team:', teamId, 'season:', seasonName);
      
      // Get current points
      const currentPoints = await pointsService.getUserPoints(userId, teamId);
      if (!currentPoints) {
        return { success: false, message: 'Could not retrieve user points' };
      }

      debugLog('Current points:', currentPoints);

      // Determine date range for this season
      const lastSnapshot = await this.getLastSeasonSnapshot(userId, teamId);
      const teamMemberJoinDate = await this.getTeamMemberJoinDate(userId, teamId);
      
      let seasonStartDate: Date;
      if (lastSnapshot) {
        seasonStartDate = lastSnapshot.resetDate.toDate();
      } else if (teamMemberJoinDate) {
        seasonStartDate = teamMemberJoinDate;
      } else {
        // Fallback to 1 year ago if we can't determine start date
        seasonStartDate = new Date();
        seasonStartDate.setFullYear(seasonStartDate.getFullYear() - 1);
      }
      
      const seasonEndDate = new Date(); // Current date
      
      debugLog('Season date range:', seasonStartDate, 'to', seasonEndDate);

      // Get all achievements, badges, and PRs (fail-soft so reset can still complete)
      const [achievements, badges, prs] = await Promise.all([
        pointsService.getUserAchievements(userId, teamId).catch((error) => {
          console.warn('[SeasonService] Failed to load achievements for snapshot; continuing with empty array:', error);
          return [] as Achievement[];
        }),
        badgeService.getUserBadges(userId, teamId).catch((error) => {
          console.warn('[SeasonService] Failed to load badges for snapshot; continuing with empty array:', error);
          return [] as UserBadge[];
        }),
        avatarService.getUserPRs(userId, teamId).catch((error) => {
          console.warn('[SeasonService] Failed to load PRs for snapshot; continuing with empty array:', error);
          return [] as PersonalRecord[];
        })
      ]);

      debugLog('Retrieved data:', {
        achievements: achievements.length,
        badges: badges.length,
        prs: prs.length
      });

      // Filter by date range
      const seasonAchievements = this.filterAchievementsByDate(achievements, seasonStartDate, seasonEndDate);
      const seasonBadges = this.filterBadgesByDate(badges, seasonStartDate, seasonEndDate);
      const seasonPRs = this.filterPRsByDate(prs, seasonStartDate, seasonEndDate);
      const totalActivities = await this.countActivitiesInRange(userId, teamId, seasonStartDate, seasonEndDate);

      debugLog('Filtered season data:', {
        achievements: seasonAchievements.length,
        badges: seasonBadges.length,
        prs: seasonPRs.length,
        activities: totalActivities
      });

      // Create season snapshot
      const snapshotData: Omit<SeasonSnapshot, 'id'> = {
        userId,
        teamId,
        seasonName,
        resetDate: Timestamp.now(),
        resetBy,
        experiencePoints: currentPoints.experiencePoints || 0,
        walletPoints: currentPoints.walletPoints || 0,
        level: currentPoints.level || 1,
        currentStreak: currentPoints.currentStreak || 0,
        longestStreak: currentPoints.longestStreak || 0,
        achievements: seasonAchievements,
        badges: seasonBadges,
        personalRecords: seasonPRs,
        totalActivities,
        createdAt: Timestamp.now()
      };

      // Save snapshot and reset points in a transaction
      if (Capacitor.getPlatform() === 'ios') {
        // On iOS, use REST API for snapshot creation
        let snapshotId = '';
        try {
          snapshotId = await firestoreRestApi.createDocument('seasonSnapshots', {
            ...snapshotData,
            resetDate: snapshotData.resetDate.toDate(),
            createdAt: snapshotData.createdAt.toDate(),
            achievements: snapshotData.achievements.map(a => ({
              ...a,
              unlockedAt: a.unlockedAt?.toDate(),
              createdAt: a.createdAt?.toDate()
            })),
            badges: snapshotData.badges.map(b => ({
              ...b,
              awardedAt: b.awardedAt?.toDate()
            })),
            personalRecords: snapshotData.personalRecords.map(pr => ({
              ...pr,
              date: pr.date?.toDate(),
              createdAt: pr.createdAt?.toDate(),
              updatedAt: pr.updatedAt?.toDate()
            }))
          });
        } catch (createError: any) {
          throw new Error(`Failed creating season snapshot: ${createError?.message || 'unknown error'}`);
        }

        // Reset points via REST API
        const pointsDocId = `${teamId}_${userId}`;
        try {
          await firestoreRestApi.setDocument('points', pointsDocId, {
            experiencePoints: 0,
            walletPoints: 0,
            level: 1,
            lastUpdated: new Date()
          }, true); // merge=true to update only these fields
        } catch (pointsError: any) {
          throw new Error(`Failed resetting points after snapshot creation: ${pointsError?.message || 'unknown error'}`);
        }

        debugLog('Season reset completed via REST API. Snapshot ID:', snapshotId);
      } else {
        // On web/Android, avoid transaction constraints and write in sequence.
        // This is more resilient for first-time resets where points docs may not exist.
        let snapshotRef: { id: string } | null = null;
        try {
          const sdkSnapshotRef = await addDoc(collection(db, 'seasonSnapshots'), snapshotData);
          snapshotRef = { id: sdkSnapshotRef.id };
        } catch (createError: any) {
          // On web, SDK auth can intermittently fail; retry snapshot creation through REST API.
          try {
            const snapshotId = await firestoreRestApi.createDocument('seasonSnapshots', {
              ...snapshotData,
              resetDate: snapshotData.resetDate.toDate(),
              createdAt: snapshotData.createdAt.toDate(),
              achievements: snapshotData.achievements.map(a => ({
                ...a,
                unlockedAt: a.unlockedAt?.toDate(),
                createdAt: a.createdAt?.toDate()
              })),
              badges: snapshotData.badges.map(b => ({
                ...b,
                awardedAt: b.awardedAt?.toDate()
              })),
              personalRecords: snapshotData.personalRecords.map(pr => ({
                ...pr,
                date: pr.date?.toDate(),
                createdAt: pr.createdAt?.toDate(),
                updatedAt: pr.updatedAt?.toDate()
              }))
            });
            snapshotRef = { id: snapshotId };
            console.warn('[SeasonService] SDK snapshot create failed; REST fallback succeeded', {
              sdkErrorCode: createError?.code,
              sdkErrorMessage: createError?.message
            });
          } catch (restCreateError: any) {
            throw new Error(
              `Failed creating season snapshot: SDK(${createError?.code || 'unknown'}: ${createError?.message || 'unknown'}) ` +
              `REST(${restCreateError?.message || 'unknown'})`
            );
          }
        }

        const pointsRef = doc(db, 'points', `${teamId}_${userId}`);
        try {
          await setDoc(pointsRef, {
            experiencePoints: 0,
            walletPoints: 0,
            level: 1,
            lastUpdated: serverTimestamp()
          }, { merge: true });
        } catch (pointsError: any) {
          // Mirror snapshot behavior: if SDK write fails, retry via REST API.
          try {
            await firestoreRestApi.setDocument('points', `${teamId}_${userId}`, {
              experiencePoints: 0,
              walletPoints: 0,
              level: 1,
              lastUpdated: new Date()
            }, true);
            console.warn('[SeasonService] SDK points reset failed; REST fallback succeeded', {
              sdkErrorCode: pointsError?.code,
              sdkErrorMessage: pointsError?.message
            });
          } catch (restPointsError: any) {
            throw new Error(
              `Failed resetting points after snapshot creation: SDK(${pointsError?.code || 'unknown'}: ${pointsError?.message || 'unknown'}) ` +
              `REST(${restPointsError?.message || 'unknown'})`
            );
          }
        }

        debugLog('Season reset completed via SDK/REST writes. Snapshot ID:', snapshotRef.id);
      }

      return { 
        success: true, 
        message: `Season "${seasonName}" reset successfully. Snapshot saved.` 
      };
    } catch (error: any) {
      console.error('Error resetting user season:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to reset season' 
      };
    }
  },

  // Reset entire team's season
  async resetTeamSeason(
    teamId: string, 
    seasonName: string, 
    resetBy: string
  ): Promise<{ success: boolean; message: string; results: { userId: string; success: boolean; message: string }[] }> {
    try {
      debugLog('Starting team season reset for team:', teamId, 'season:', seasonName);
      
      // Get all team members
      const members = await teamService.getTeamMembers(teamId);
      debugLog('Team members to reset:', members.length);

      const results: { userId: string; success: boolean; message: string }[] = [];
      
      // Reset each member
      for (const member of members) {
        try {
          const result = await this.resetUserSeason(member.athleteId, teamId, seasonName, resetBy);
          results.push({
            userId: member.athleteId,
            success: result.success,
            message: result.message
          });
        } catch (error: any) {
          results.push({
            userId: member.athleteId,
            success: false,
            message: error.message || 'Failed to reset season'
          });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      return {
        success: failCount === 0,
        message: `Reset completed: ${successCount} successful, ${failCount} failed`,
        results
      };
    } catch (error: any) {
      console.error('Error resetting team season:', error);
      return {
        success: false,
        message: error.message || 'Failed to reset team season',
        results: []
      };
    }
  },

  // Get user's season history
  async getUserSeasonHistory(userId: string, teamId: string): Promise<SeasonSnapshot[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'seasonSnapshots',
          [
            { field: 'userId', op: '==', value: userId },
            { field: 'teamId', op: '==', value: teamId }
          ]
        );
        
        // Sort by resetDate descending
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().resetDate?.seconds || 0;
          const bTime = b.data().resetDate?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            resetDate: data.resetDate ? Timestamp.fromDate(new Date(data.resetDate.seconds * 1000)) : Timestamp.now(),
            createdAt: data.createdAt ? Timestamp.fromDate(new Date(data.createdAt.seconds * 1000)) : Timestamp.now(),
          } as SeasonSnapshot;
        });
      }
      
      // On web/Android, try SDK first, fallback to REST API on permission errors
      try {
        const q = query(
          collection(db, 'seasonSnapshots'),
          where('userId', '==', userId),
          where('teamId', '==', teamId),
          orderBy('resetDate', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          resetDate: doc.data().resetDate,
          createdAt: doc.data().createdAt,
        })) as SeasonSnapshot[];
      } catch (sdkError: any) {
        // If SDK fails with permission error, try REST API as fallback
        if (sdkError.code === 'permission-denied' || sdkError.message?.includes('permission')) {
          console.log('[SeasonService] SDK permission error, falling back to REST API');
          const documents = await firestoreRestApi.queryDocuments(
            'seasonSnapshots',
            [
              { field: 'userId', op: '==', value: userId },
              { field: 'teamId', op: '==', value: teamId }
            ]
          );
          
          // Sort by resetDate descending
          const sorted = documents.sort((a, b) => {
            const aTime = a.data().resetDate?.seconds || 0;
            const bTime = b.data().resetDate?.seconds || 0;
            return bTime - aTime;
          });
          
          return sorted.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              resetDate: data.resetDate ? Timestamp.fromDate(new Date(data.resetDate.seconds * 1000)) : Timestamp.now(),
              createdAt: data.createdAt ? Timestamp.fromDate(new Date(data.createdAt.seconds * 1000)) : Timestamp.now(),
            } as SeasonSnapshot;
          });
        }
        throw sdkError; // Re-throw if it's not a permission error
      }
    } catch (error) {
      console.error('Error getting user season history:', error);
      return [];
    }
  },

  // Get specific season snapshot details
  async getSeasonSnapshot(snapshotId: string): Promise<SeasonSnapshot | null> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const doc = await firestoreRestApi.getDocument('seasonSnapshots', snapshotId);
        if (!doc) return null;
        
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          resetDate: data.resetDate ? Timestamp.fromDate(new Date(data.resetDate.seconds * 1000)) : Timestamp.now(),
          createdAt: data.createdAt ? Timestamp.fromDate(new Date(data.createdAt.seconds * 1000)) : Timestamp.now(),
        } as SeasonSnapshot;
      }
      
      // On web/Android, try SDK first, fallback to REST API on permission errors
      try {
        const snapshotRef = doc(db, 'seasonSnapshots', snapshotId);
        const snapshotDoc = await getDoc(snapshotRef);
        
        if (!snapshotDoc.exists()) {
          return null;
        }
        
        return {
          id: snapshotDoc.id,
          ...snapshotDoc.data(),
          resetDate: snapshotDoc.data().resetDate,
          createdAt: snapshotDoc.data().createdAt,
        } as SeasonSnapshot;
      } catch (sdkError: any) {
        // If SDK fails with permission error, try REST API as fallback
        if (sdkError.code === 'permission-denied' || sdkError.message?.includes('permission')) {
          console.log('[SeasonService] SDK permission error, falling back to REST API');
          const doc = await firestoreRestApi.getDocument('seasonSnapshots', snapshotId);
          if (!doc) return null;
          
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            resetDate: data.resetDate ? Timestamp.fromDate(new Date(data.resetDate.seconds * 1000)) : Timestamp.now(),
            createdAt: data.createdAt ? Timestamp.fromDate(new Date(data.createdAt.seconds * 1000)) : Timestamp.now(),
          } as SeasonSnapshot;
        }
        throw sdkError; // Re-throw if it's not a permission error
      }
    } catch (error) {
      console.error('Error getting season snapshot:', error);
      return null;
    }
  }
};

